import { QuoteRequest, QuoteResponse, StoredQuote } from '@/types/quote';
import { openDB, IDBPDatabase } from 'idb';
import { supabase } from '@/integrations/supabase/client';

class QuoteService {
  private db: IDBPDatabase | null = null;

  // Initialiser IndexedDB pour le stockage local
  private async initDB() {
    if (this.db) return this.db;
    
    this.db = await openDB('QuotesDB', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('quotes')) {
          const store = db.createObjectStore('quotes', { keyPath: 'id' });
          store.createIndex('created_at', 'created_at');
          store.createIndex('status', 'status');
        }
      },
    });
    
    return this.db;
  }

  // Générer une clé d'idempotence
  private generateIdempotencyKey(payload: QuoteRequest): string {
    const str = `${payload.customer.email}_${payload.meta.cart_id || 'single'}_${payload.trigger}`;
    return btoa(str).replace(/[^a-zA-Z0-9]/g, '').substring(0, 32);
  }

  // Créer un devis via l'edge function appropriée
  async createQuote(payload: QuoteRequest, retryCount = 0): Promise<QuoteResponse> {
    try {
      console.log('📤 Envoi demande devis:', payload);

      // Récupérer l'utilisateur connecté pour l'ID
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('❌ Erreur récupération utilisateur:', userError);
        throw new Error('Utilisateur non connecté');
      }

      // Déterminer quelle edge function utiliser selon le mode de financement
      const shouldUseMake = payload.funding?.mode === 'OPCO' || payload.funding?.mode === 'VIREMENT';
      const functionName = shouldUseMake ? 'create-quote-make' : 'create-quote';
      
      console.log(`🎯 Utilisation de la fonction: ${functionName} pour le mode: ${payload.funding?.mode}`);

      // Ajouter l'ID utilisateur au payload
      const payloadWithUser = {
        ...payload,
        user_id: user?.id
      };

      console.log('👤 ID utilisateur ajouté:', user?.id);

      // Appel de l'edge function appropriée
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: payloadWithUser
      });

      if (error) throw error;

      const result: QuoteResponse = data;
      console.log('✅ Réponse function reçue:', result);

      // Stocker localement pour compatibilité
      await this.storeQuoteLocally({
        id: result.quote_id,
        number: result.quote_number,
        status: 'sent',
        customer_name: `${payload.customer.contact_first_name} ${payload.customer.contact_last_name}`,
        total_ttc: payload.totals.total_ttc,
        currency: payload.totals.currency,
        pdf_url: result.quote_pdf_url,
        signature_url: result.signature_url,
        created_at: new Date().toISOString(),
        expires_at: result.expires_at,
        trigger: payload.trigger,
        training_title: payload.training?.title,
        participants_count: payload.meta?.participants_count,
      });

      // Tracker l'événement GTM
      this.trackQuoteEvent(payload.trigger, result.quote_id, payload.totals.total_ttc, payload.funding.mode);

      return result;
    } catch (error) {
      console.error('❌ Erreur création devis:', error);
      
      // Retry logic (max 2 retries)
      if (retryCount < 2) {
        console.log(`🔄 Tentative ${retryCount + 1}/2...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Backoff
        return this.createQuote(payload, retryCount + 1);
      }
      
      throw error;
    }
  }

  // Stocker un devis en local
  private async storeQuoteLocally(quote: StoredQuote) {
    try {
      const db = await this.initDB();
      await db.put('quotes', quote);
      console.log('💾 Devis stocké localement:', quote.id);
    } catch (error) {
      console.error('❌ Erreur stockage local:', error);
    }
  }

  // Récupérer les devis stockés localement
  async getStoredQuotes(): Promise<StoredQuote[]> {
    try {
      const db = await this.initDB();
      const quotes = await db.getAll('quotes');
      return quotes.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } catch (error) {
      console.error('❌ Erreur récupération devis locaux:', error);
      return [];
    }
  }

  // Récupérer un devis par ID
  async getQuoteById(id: string): Promise<StoredQuote | null> {
    try {
      const db = await this.initDB();
      const quote = await db.get('quotes', id);
      return quote || null;
    } catch (error) {
      console.error('❌ Erreur récupération devis:', error);
      return null;
    }
  }

  // Mettre à jour le statut d'un devis
  async updateQuoteStatus(id: string, status: StoredQuote['status']) {
    try {
      const db = await this.initDB();
      const quote = await db.get('quotes', id);
      if (quote) {
        quote.status = status;
        await db.put('quotes', quote);
        console.log(`✅ Statut devis ${id} mis à jour: ${status}`);
      }
    } catch (error) {
      console.error('❌ Erreur mise à jour statut:', error);
    }
  }

  // Tracker les événements GTM
  private trackQuoteEvent(trigger: string, quoteId: string, amount: number, mode: string) {
    if (typeof window !== 'undefined' && window.dataLayer) {
      const eventMap = {
        'quote_from_product': 'lead.new',
        'checkout.intent:quote': 'checkout.intent_quote',
        'intra.request': 'intra.request',
        'cart.export.quote': 'cart.export_quote',
      };

      const event = eventMap[trigger as keyof typeof eventMap] || 'quote.created';
      
      window.dataLayer.push({
        event,
        quote_id: quoteId,
        amount,
        funding_mode: mode,
        timestamp: new Date().toISOString(),
      });

      console.log('📊 Événement GTM envoyé:', event, { quote_id: quoteId, amount, funding_mode: mode });
    }
  }

  // Nettoyer les anciens devis (> 30 jours)
  async cleanupOldQuotes() {
    try {
      const db = await this.initDB();
      const quotes = await db.getAll('quotes');
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      for (const quote of quotes) {
        if (new Date(quote.created_at) < thirtyDaysAgo) {
          await db.delete('quotes', quote.id);
          console.log('🗑️ Devis ancien supprimé:', quote.id);
        }
      }
    } catch (error) {
      console.error('❌ Erreur nettoyage devis:', error);
    }
  }
}

export const quoteService = new QuoteService();

// Déclarer le type pour window.dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}